import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-polylinedecorator';

// --- LEAFLET ICON FIX ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const AdminDashboard = () => {
  const [fleetData, setFleetData] = useState([]);
  const [godViewData, setGodViewData] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ 
    totalCommission: 0, 
    activeAlerts: 0, 
    activeFleetValue: 0,
    totalFleetValue: 0,
    onlineFleetValue: 0 
  });
  const [transactions, setTransactions] = useState([]);
  const [emergencyLocation, setEmergencyLocation] = useState(null);
  const [activeEmergencies, setActiveEmergencies] = useState([]);
  
  // Emergency trigger state
  const [isEmergencyTriggering, setIsEmergencyTriggering] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState('');
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [selectedEmergencyDriver, setSelectedEmergencyDriver] = useState(null);
  
  // Panel visibility states
  const [isFleetPanelOpen, setIsFleetPanelOpen] = useState(true);
  const [isGoldminePanelOpen, setIsGoldminePanelOpen] = useState(true);
  const [isFullscreenMap, setIsFullscreenMap] = useState(false);
  
  // Map references
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const routesRef = useRef({});
  const mapInitializedRef = useRef(false);
  const isFirstLoadRef = useRef(true);

  // Dispatch Modal State
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({
    origin: '',
    destination: '',
    originLat: '',
    originLng: '',
    destLat: '',
    destLng: '',
    selectedDriverId: '',
    requiresTrailer: false,
    inventory: {
      boxes: 0,
      fridge_double_door: 0,
      washing_machine: 0,
      queen_bed: 0
    },
    special_instructions: ''
  });
  const [originCoordinates, setOriginCoordinates] = useState(null);
  const [destCoordinates, setDestCoordinates] = useState(null);
  const [geocodingOrigin, setGeocodingOrigin] = useState(false);
  const [geocodingDest, setGeocodingDest] = useState(false);

  // Emergency Alert State
  const [emergencyAlert, setEmergencyAlert] = useState(null);

  // --- CUSTOM ICONS FOR MAP ---
  const getDriverIcon = (status, color, isEmergency = false) => {
    const iconSize = status === 'on trip' ? 32 : 24;
    const pulseClass = isEmergency ? 'emergency-pulse' : '';
    
    return L.divIcon({
      className: `custom-driver-marker ${pulseClass}`,
      html: `
        <div style="
          width: ${iconSize}px;
          height: ${iconSize}px;
          background: ${isEmergency ? '#ef4444' : (color || '#4CAF50')};
          border-radius: 50%;
          border: ${isEmergency ? '4px solid #ff0000' : '3px solid white'};
          box-shadow: ${isEmergency ? '0 0 20px rgba(255,0,0,0.8)' : '0 2px 8px rgba(0,0,0,0.3)'};
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
          transition: all 0.3s;
          animation: ${isEmergency ? 'emergencyPulse 0.5s ease-in-out infinite alternate' : 'none'};
        ">
          ${isEmergency ? '🆘' : (status === 'on trip' ? '🚛' : '🚗')}
        </div>
      `,
      iconSize: [iconSize, iconSize],
      iconAnchor: [iconSize/2, iconSize/2]
    });
  };

  // --- MAP FUNCTIONS ---
  const initializeMap = (drivers) => {
    if (!mapRef.current) {
      mapRef.current = L.map('admin-map', {
        center: [-26.2041, 28.0473],
        zoom: 10,
        zoomControl: true,
        fadeAnimation: true,
        zoomAnimation: true,
        zoomSnap: 0.5,
        zoomDelta: 0.5
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapRef.current);
      
      mapInitializedRef.current = true;
      isFirstLoadRef.current = true;
    }
    updateMapWithDrivers(drivers);
  };

  const updateMapWithDrivers = (drivers) => {
    const map = mapRef.current;
    if (!map) return;

    // Clear existing markers and routes
    Object.values(markersRef.current).forEach(marker => map.removeLayer(marker));
    Object.values(routesRef.current).forEach(route => map.removeLayer(route));
    markersRef.current = {};
    routesRef.current = {};

    drivers.forEach((driver) => {
      if (!driver.lat || !driver.lng) return;

      // Check if driver has active emergency
      const hasEmergency = activeEmergencies.some(e => e.driver_id === driver.id);

      // Create marker with emergency styling
      const marker = L.marker([driver.lat, driver.lng], {
        icon: getDriverIcon(driver.driver_status, driver.color, hasEmergency)
      })
      .bindPopup(`
        <div style="font-family: Arial, sans-serif; min-width: 200px; color: #111827;">
          <strong style="color: ${hasEmergency ? '#ef4444' : (driver.color || '#eab308')};">${hasEmergency ? '🚨 ' : ''}${driver.driver_name}</strong><br/>
          Status: ${hasEmergency ? '🚨 EMERGENCY ACTIVE' : driver.driver_status.toUpperCase()}<br/>
          Balance: R${driver.wallet_balance_zar || '0.00'}<br/>
          ${driver.has_trailer ? '🔗 Trailer Attached<br/>' : ''}
          ${hasEmergency ? `<span style="color: #ef4444; font-weight: bold;">⚠️ EMERGENCY LOCATE ACTIVE</span><br/>` : ''}
          ${driver.active_job ? `
            <hr/>
            📦 Active Job:<br/>
            ${driver.job_status === 'picked_up' ? '📦 To Drop-off' : '📍 To Pickup'}<br/>
            <small>${driver.active_job.pickup?.substring(0, 30) || ''} → ${driver.active_job.dropoff?.substring(0, 30) || ''}</small>
          ` : '🚗 Available'}
          ${hasEmergency ? `<hr/><button onclick="window.triggerEmergency('${driver.id}')" style="background:#ef4444;color:white;border:none;padding:6px 12px;border-radius:4px;cursor:pointer;font-weight:bold;">🆘 TRIGGER EMERGENCY</button>` : ''}
        </div>
      `)
      .addTo(map);
      markersRef.current[driver.id] = marker;

      // Draw route if driver has one
      if (driver.route && driver.route.coordinates && driver.route.coordinates.length > 0) {
        const routePoints = driver.route.coordinates.map(coord => [coord.lat, coord.lng]);
        
        const routeLine = L.polyline(routePoints, {
          color: hasEmergency ? '#ef4444' : (driver.color || '#eab308'),
          weight: hasEmergency ? 5 : 3,
          opacity: hasEmergency ? 1 : 0.8,
          lineJoin: 'round',
          dashArray: hasEmergency ? '10, 5' : null
        })
        .bindPopup(`
          <div style="font-family: Arial, sans-serif; color: #111827;">
            <strong style="color: ${hasEmergency ? '#ef4444' : (driver.color || '#eab308')};">${driver.driver_name}'s Route</strong><br/>
            Distance: ${driver.route.distance ? `${driver.route.distance.toFixed(1)} km` : 'N/A'}<br/>
            ETA: ${driver.route.duration ? `${Math.round(driver.route.duration)} min` : 'N/A'}
            ${hasEmergency ? '<br/><span style="color:#ef4444;font-weight:bold;">🚨 EMERGENCY ROUTE</span>' : ''}
          </div>
        `)
        .addTo(map);
        routesRef.current[driver.id] = routeLine;

        // Add pickup/dropoff markers
        if (driver.active_job) {
          if (driver.pickup && driver.pickup.lat) {
            const pickupMarker = L.circleMarker([driver.pickup.lat, driver.pickup.lng], {
              radius: 8,
              color: '#4CAF50',
              fillColor: '#4CAF50',
              fillOpacity: 0.7
            })
            .bindPopup(`📍 Pickup: ${driver.pickup.address?.substring(0, 50) || 'Pickup'}`)
            .addTo(map);
            markersRef.current[`${driver.id}_pickup`] = pickupMarker;
          }

          if (driver.dropoff && driver.dropoff.lat) {
            const dropoffMarker = L.circleMarker([driver.dropoff.lat, driver.dropoff.lng], {
              radius: 8,
              color: '#F44336',
              fillColor: '#F44336',
              fillOpacity: 0.7
            })
            .bindPopup(`📦 Dropoff: ${driver.dropoff.address?.substring(0, 50) || 'Dropoff'}`)
            .addTo(map);
            markersRef.current[`${driver.id}_dropoff`] = dropoffMarker;
          }
        }
      }
    });

    // Only fit bounds on first load, never again
    if (isFirstLoadRef.current && !emergencyLocation) {
      const allMarkers = Object.values(markersRef.current);
      if (allMarkers.length > 0) {
        const group = L.featureGroup(allMarkers);
        map.fitBounds(group.getBounds().pad(0.1));
        isFirstLoadRef.current = false;
      }
    }
  };

  // --- TOGGLE FULLSCREEN MAP ---
  const toggleFullscreenMap = () => {
    setIsFullscreenMap(!isFullscreenMap);
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 300);
  };

  // --- GEOCODING FUNCTIONS ---
  const geocodeAddress = async (address) => {
    if (!address || address.trim() === '') return null;
    try {
      const encodedAddress = encodeURIComponent(address);
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`);
      const data = await response.json();
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          display_name: data[0].display_name
        };
      }
      return null;
    } catch (err) {
      console.error("Geocoding error:", err);
      return null;
    }
  };

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const handleOriginAddressChange = async (address) => {
    setDispatchForm(prev => ({...prev, origin: address}));
    if (address && address.trim() !== '') {
      setGeocodingOrigin(true);
      const coords = await geocodeAddress(address);
      if (coords) {
        setOriginCoordinates(coords);
        setDispatchForm(prev => ({
          ...prev,
          origin: address,
          originLat: coords.lat.toString(),
          originLng: coords.lng.toString()
        }));
      } else {
        setOriginCoordinates(null);
        setDispatchForm(prev => ({ ...prev, originLat: '', originLng: '' }));
      }
      setGeocodingOrigin(false);
    }
  };

  const handleDestAddressChange = async (address) => {
    setDispatchForm(prev => ({...prev, destination: address}));
    if (address && address.trim() !== '') {
      setGeocodingDest(true);
      const coords = await geocodeAddress(address);
      if (coords) {
        setDestCoordinates(coords);
        setDispatchForm(prev => ({
          ...prev,
          destination: address,
          destLat: coords.lat.toString(),
          destLng: coords.lng.toString()
        }));
      } else {
        setDestCoordinates(null);
        setDispatchForm(prev => ({ ...prev, destLat: '', destLng: '' }));
      }
      setGeocodingDest(false);
    }
  };

  const debouncedOriginGeocode = debounce(handleOriginAddressChange, 1000);
  const debouncedDestGeocode = debounce(handleDestAddressChange, 1000);

  // ==================== EMERGENCY FUNCTIONS ====================
  
  // Fetch active emergencies
  const fetchActiveEmergencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/jobs/emergency/active', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setActiveEmergencies(data.emergencies || []);
          
          // Update stats
          setStats(prev => ({
            ...prev,
            activeAlerts: data.emergencies?.length || 0
          }));
        }
      }
    } catch (err) {
      console.error('Fetch emergencies error:', err);
    }
  };

  // Trigger emergency locate
  const triggerEmergencyLocate = async (driverId, driverName, reason) => {
    if (!driverId) {
      alert('Please select a driver first');
      return;
    }

    if (!window.confirm(
      `🚨 WARNING: This will trigger an emergency locate on ${driverName}'s phone.\n\n` +
      `The phone will:\n` +
      `• Ring loudly (bypasses silent mode)\n` +
      `• Display an emergency alert\n` +
      `• Share GPS location in real-time\n\n` +
      `Continue?`
    )) {
      return;
    }

    setIsEmergencyTriggering(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/jobs/emergency/locate/${driverId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: reason || 'Driver safety concern - Admin initiated'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`✅ Emergency locate triggered for ${driverName}!\n\n${data.message || 'Phone will ring loudly and GPS tracking is active.'}`);
        
        // Refresh data
        fetchGodView();
        fetchActiveEmergencies();
        
        // Close emergency modal
        setShowEmergencyModal(false);
        setSelectedEmergencyDriver(null);
        setEmergencyReason('');
        
        // Focus map on driver
        const driver = fleetData.find(d => d.id === driverId);
        if (driver && driver.lat && driver.lng && mapRef.current) {
          mapRef.current.flyTo([driver.lat, driver.lng], 16, { animate: true, duration: 2 });
        }
      } else {
        alert('❌ Failed to trigger emergency locate: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Emergency locate error:', err);
      alert('❌ Network error: ' + err.message);
    } finally {
      setIsEmergencyTriggering(false);
    }
  };

  // Resolve emergency
  const resolveEmergency = async (emergencyId, driverName) => {
    if (!window.confirm(`Resolve emergency for ${driverName}?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/jobs/emergency/resolve/${emergencyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          notes: 'Resolved by admin'
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(`✅ Emergency resolved for ${driverName}`);
        fetchActiveEmergencies();
        fetchGodView();
      } else {
        alert('❌ Failed to resolve emergency: ' + data.message);
      }
    } catch (err) {
      console.error('Resolve emergency error:', err);
      alert('❌ Network error: ' + err.message);
    }
  };

  // --- API FUNCTIONS ---
  const fetchGodView = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No admin token found');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/admin/god-view', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('God view response not OK:', response.status);
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('📊 God View Data:', data);

      if (data.success) {
        setGodViewData(data);
        const fleet = data.fleet || [];
        setFleetData(fleet);
        setTransactions(data.ledger || []);
        
        setStats(prev => ({
          ...prev,
          totalCommission: data.total_commission || 0,
          activeFleetValue: data.active_fleet_value || 0,
          totalFleetValue: data.total_fleet_value || data.active_fleet_value || 0,
          onlineFleetValue: data.online_fleet_value || 0
        }));

        if (fleet.length > 0) {
          initializeMap(fleet);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error("Fetch Error:", err.message);
      setLoading(false);
    }
  };

  const toggleTrailerMode = async (driverId, currentIsTrailer) => {
    if (!driverId) return;
    const targetStatus = !currentIsTrailer;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/trailers/toggle/${driverId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ active_status: targetStatus })
      });
      const data = await response.json();
      if (data.success) {
        console.log("✅ Database record updated!");
        fetchGodView();
      }
    } catch (err) {
      console.error("Toggle Error:", err);
    }
  };

  // --- DISPATCH JOB ---
  const createCustomerRequest = async () => {
    if (!dispatchForm.origin || !dispatchForm.destination) {
      alert('Please enter both pickup and delivery addresses');
      return;
    }
    if (!dispatchForm.originLat || !dispatchForm.originLng) {
      alert('Please wait for coordinates to load for pickup address');
      return;
    }
    if (!dispatchForm.destLat || !dispatchForm.destLng) {
      alert('Please wait for coordinates to load for delivery address');
      return;
    }
    if (!dispatchForm.selectedDriverId) {
      alert('Please select a driver');
      return;
    }

    const selectedDriver = fleetData.find(d => d.id === dispatchForm.selectedDriverId);
    if (dispatchForm.requiresTrailer && !selectedDriver?.has_trailer) {
      alert(`⚠️ ${selectedDriver?.driver_name} does NOT have a trailer!\n\nPlease either:\n1. Click "ADD TRAILER" on that driver first\n2. Select a different driver who has a trailer`);
      return;
    }

    const inventoryToSend = {};
    Object.keys(dispatchForm.inventory).forEach(item => {
      if (dispatchForm.inventory[item] > 0) {
        inventoryToSend[item] = dispatchForm.inventory[item];
      }
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/jobs/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: localStorage.getItem('userId') || 'temp_customer',
          pickup_address: dispatchForm.origin,
          dropoff_address: dispatchForm.destination,
          pickup_lat: parseFloat(dispatchForm.originLat),
          pickup_lng: parseFloat(dispatchForm.originLng),
          dropoff_lat: parseFloat(dispatchForm.destLat),
          dropoff_lng: parseFloat(dispatchForm.destLng),
          inventory: inventoryToSend,
          vehicle_type: dispatchForm.requiresTrailer ? 'Van + Trailer' : 'Van',
          has_trailer: dispatchForm.requiresTrailer,
          special_instructions: dispatchForm.special_instructions,
          driver_id: dispatchForm.selectedDriverId
        })
      });
      const data = await response.json();
      if (data.success) {
        console.log(`✅ Job created! Distance: ${data.calculated_distance_km}km, Price: R${data.final_price_zar}`);
        fetchGodView();
        setShowDispatchModal(false);
        setDispatchForm({
          origin: '',
          destination: '',
          originLat: '',
          originLng: '',
          destLat: '',
          destLng: '',
          selectedDriverId: '',
          requiresTrailer: false,
          inventory: { boxes: 0, fridge_double_door: 0, washing_machine: 0, queen_bed: 0 },
          special_instructions: ''
        });
        setOriginCoordinates(null);
        setDestCoordinates(null);
        alert(`✅ JOB CREATED SUCCESSFULLY!\n\n📍 Route: ${dispatchForm.origin} → ${dispatchForm.destination}\n💰 Total Price: R${data.final_price_zar}\n🔐 Safety PIN: ${data.verification_pin}\n\nGive this PIN to the driver at pickup!`);
      } else {
        alert('Failed to create job: ' + (data.detail || data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error("Dispatch Error:", err);
      alert('Error creating job. Check console for details.');
    }
  };

  // --- EFFECTS ---
  useEffect(() => {
    fetchGodView();
    fetchActiveEmergencies();
    
    // Refresh every 10 seconds
    const interval = setInterval(() => {
      fetchGodView();
      fetchActiveEmergencies();
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const emergencyDriver = fleetData.find(d => 
      activeEmergencies.some(e => e.driver_id === d.id)
    );
    
    if (emergencyDriver && emergencyDriver.lat) {
      console.warn("⚠️ EMERGENCY DETECTED for:", emergencyDriver.driver_name);
      setEmergencyLocation([emergencyDriver.lat, emergencyDriver.lng]);
      if (mapRef.current) {
        mapRef.current.flyTo([emergencyDriver.lat, emergencyDriver.lng], 14, { animate: true, duration: 2 });
      }
    }
  }, [fleetData, activeEmergencies]);

  useEffect(() => {
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 400);
  }, [isFleetPanelOpen, isGoldminePanelOpen, isFullscreenMap]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return <div className="h-screen bg-gray-900 text-yellow-500 flex items-center justify-center font-black italic text-2xl">LOADING ANDROMEDA...</div>;
  }

  const getStatusBadge = (driver) => {
    const hasEmergency = activeEmergencies.some(e => e.driver_id === driver.id);
    if (hasEmergency) return { text: '🚨 EMERGENCY', color: '#ef4444' };
    const status = driver.driver_status || (driver.is_online ? 'online' : 'offline');
    if (status === 'online') return { text: '🟢 Online', color: '#10b981' };
    if (status === 'on trip') return { text: '🟡 On Trip', color: '#eab308' };
    return { text: '🔴 Offline', color: '#ef4444' };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', backgroundColor: '#111827', color: 'white', overflow: 'hidden' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', gap: '20px', padding: '15px', backgroundColor: '#1f2937', borderBottom: '2px solid #eab308', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ flex: 1, backgroundColor: '#374151', padding: '10px', borderRadius: '8px', borderLeft: '4px solid #eab308' }}>
          <span style={{ fontSize: '11px', color: '#9ca3af', display: 'block' }}>TOTAL COMMISSION (20%)</span>
          <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#facc15' }}>R {Number(stats.totalCommission).toLocaleString()}</span>
        </div>
        <div style={{ flex: 1, backgroundColor: '#374151', padding: '10px', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
          <span style={{ fontSize: '11px', color: '#9ca3af', display: 'block' }}>ACTIVE FLEET VALUE</span>
          <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#60a5fa' }}>R {Number(stats.activeFleetValue || stats.totalFleetValue || 0).toLocaleString()}</span>
        </div>
        <div style={{ flex: 1, backgroundColor: '#374151', padding: '10px', borderRadius: '8px', borderLeft: '4px solid #ef4444' }}>
          <span style={{ fontSize: '11px', color: '#9ca3af', display: 'block' }}>EMERGENCY ALERTS</span>
          <span style={{ fontSize: '22px', fontWeight: 'bold', color: activeEmergencies.length > 0 ? '#ff4444' : '#10b981' }}>
            {activeEmergencies.length > 0 ? `🚨 ${activeEmergencies.length} ACTIVE` : '✅ CLEAR'}
          </span>
        </div>
        <div style={{ flex: 1, backgroundColor: '#374151', padding: '10px', borderRadius: '8px', borderLeft: '4px solid #8b5cf6' }}>
          <span style={{ fontSize: '11px', color: '#9ca3af', display: 'block' }}>ACTIVE DRIVERS</span>
          <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#a78bfa' }}>
            {fleetData.filter(d => d.is_online).length} / {fleetData.length}
          </span>
        </div>
        <button onClick={() => setShowDispatchModal(true)} style={{ backgroundColor: '#eab308', color: 'black', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', boxShadow: '0 4px 14px 0 rgba(234, 179, 8, 0.39)' }}>
          + NEW CUSTOMER JOB
        </button>
        <button 
          onClick={toggleFullscreenMap} 
          style={{ 
            backgroundColor: isFullscreenMap ? '#ef4444' : '#3b82f6', 
            color: 'white', 
            border: 'none', 
            padding: '12px 20px', 
            borderRadius: '8px', 
            cursor: 'pointer', 
            fontWeight: 'bold', 
            fontSize: '14px',
            boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.39)'
          }}
        >
          {isFullscreenMap ? '✕ EXIT FULL MAP' : '⛶ FULL MAP'}
        </button>
      </div>

      {/* EMERGENCY BANNER - Shows when there are active emergencies */}
      {activeEmergencies.length > 0 && (
        <div style={{ 
          backgroundColor: '#450a0a', 
          borderBottom: '2px solid #ef4444',
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px', animation: 'emergencyPulse 0.5s infinite' }}>🚨</span>
            <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
              {activeEmergencies.length} ACTIVE EMERGENC{activeEmergencies.length > 1 ? 'IES' : 'Y'}
            </span>
            <span style={{ color: '#fca5a5', fontSize: '14px' }}>
              {activeEmergencies.map(e => e.driver_name).join(', ')}
            </span>
          </div>
          <button 
            onClick={() => {
              const emergency = activeEmergencies[0];
              if (emergency && emergency.driver_id) {
                const driver = fleetData.find(d => d.id === emergency.driver_id);
                if (driver && driver.lat && driver.lng && mapRef.current) {
                  mapRef.current.flyTo([driver.lat, driver.lng], 16);
                }
              }
            }}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '6px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            📍 LOCATE DRIVER
          </button>
        </div>
      )}

      {/* MAIN LAYOUT */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* LEFT COLUMN: FLEET STATUS - Collapsible */}
        <div style={{ 
          width: isFleetPanelOpen ? '380px' : '0px',
          minWidth: isFleetPanelOpen ? '380px' : '0px',
          backgroundColor: '#1f2937',
          overflow: 'hidden',
          transition: 'width 0.3s ease, min-width 0.3s ease',
          borderRight: isFleetPanelOpen ? '1px solid #374151' : 'none',
          position: 'relative'
        }}>
          <div style={{ 
            height: '100%', 
            overflowY: 'auto', 
            padding: isFleetPanelOpen ? '20px' : '0',
            opacity: isFleetPanelOpen ? 1 : 0,
            transition: 'opacity 0.2s ease'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#eab308', fontSize: '1.5rem', fontWeight: '900', fontStyle: 'italic', margin: 0 }}>
                FLEET STATUS ({fleetData.length})
              </h2>
              <button 
                onClick={() => setIsFleetPanelOpen(false)}
                style={{ 
                  backgroundColor: '#374151', 
                  color: '#9ca3af', 
                  border: 'none', 
                  padding: '4px 10px', 
                  borderRadius: '6px', 
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ◀
              </button>
            </div>
            {fleetData.length === 0 ? (
              <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px 0' }}>No drivers found in the system.</p>
            ) : (
              fleetData.map((driver, index) => {
                const isT = driver.has_trailer === true || String(driver.has_trailer).toLowerCase() === 't';
                const hasEmergency = activeEmergencies.some(e => e.driver_id === driver.id);
                const statusBadge = getStatusBadge(driver);
                return (
                  <div 
                    key={`side-${driver.id}-${index}`} 
                    style={{ 
                      backgroundColor: hasEmergency ? '#450a0a' : 'rgba(55, 65, 81, 0.5)', 
                      padding: '15px', 
                      borderRadius: '8px', 
                      marginBottom: '10px', 
                      border: hasEmergency ? '3px solid #ef4444' : `4px solid ${driver.color || '#eab308'}`,
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    onClick={() => {
                      setSelectedDriver(driver);
                      if (mapRef.current && driver.lat && driver.lng) {
                        mapRef.current.setView([driver.lat, driver.lng], 14);
                      }
                    }}
                  >
                    {hasEmergency && (
                      <div style={{ 
                        position: 'absolute', 
                        top: '-8px', 
                        right: '-8px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        animation: 'emergencyPulse 0.5s infinite'
                      }}>
                        🚨 EMERGENCY
                      </div>
                    )}
                    <div style={{ fontWeight: 'bold', marginBottom: '10px', color: hasEmergency ? '#ff4444' : '#f3f4f6' }}>
                      {hasEmergency ? '🚨 ' : ''}{driver.driver_name || 'Driver'}
                      {driver.has_trailer && ' 🔗'}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ color: '#eab308', fontWeight: 'bold' }}>R{driver.wallet_balance_zar || '0.00'}</span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTrailerMode(driver.id, isT);
                        }} 
                        style={{ 
                          backgroundColor: isT ? '#dc2626' : '#eab308', 
                          color: isT ? 'white' : 'black', 
                          border: 'none', 
                          padding: '8px 12px', 
                          borderRadius: '6px', 
                          cursor: 'pointer', 
                          fontWeight: 'bold', 
                          fontSize: '11px' 
                        }}
                      >
                        {isT ? 'REMOVE' : 'ADD'} TRAILER
                      </button>
                    </div>
                    <div style={{ fontSize: '11px', color: statusBadge.color, marginBottom: '6px' }}>
                      {statusBadge.text}
                    </div>
                    {(driver.lat || driver.lng) && (
                      <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '4px' }}>
                        📍 {driver.lat?.toFixed(4)}, {driver.lng?.toFixed(4)}
                      </div>
                    )}
                    {driver.active_job && (
                      <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px', padding: '4px 0', borderTop: '1px solid #374151' }}>
                        🚛 {driver.job_status === 'picked_up' ? '📦 To Drop-off' : '📍 To Pickup'}: {driver.active_job.pickup?.substring(0, 20)} → {driver.active_job.dropoff?.substring(0, 20)}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEmergencyDriver(driver);
                          setEmergencyReason('');
                          setShowEmergencyModal(true);
                        }} 
                        style={{ 
                          flex: 1, 
                          backgroundColor: '#ef4444', 
                          color: 'white', 
                          border: 'none', 
                          padding: '6px', 
                          borderRadius: '6px', 
                          cursor: 'pointer', 
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}
                      >
                        🆘 EMERGENCY LOCATE
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {/* Open button when collapsed */}
          {!isFleetPanelOpen && (
            <button 
              onClick={() => setIsFleetPanelOpen(true)}
              style={{
                position: 'absolute',
                top: '50%',
                left: '0',
                transform: 'translateY(-50%)',
                backgroundColor: '#374151',
                color: '#eab308',
                border: 'none',
                padding: '12px 6px',
                borderRadius: '0 8px 8px 0',
                cursor: 'pointer',
                writingMode: 'vertical-rl',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              ▶ FLEET
            </button>
          )}
        </div>

        {/* CENTER COLUMN: MAP */}
        <div style={{ 
          flex: 1, 
          position: 'relative',
          transition: 'flex 0.3s ease'
        }}>
          <div id="admin-map" style={{ height: '100%', width: '100%', backgroundColor: '#1a1a1a' }} />
          <div style={{ position: 'absolute', bottom: '20px', left: '20px', backgroundColor: 'rgba(0,0,0,0.7)', padding: '8px 12px', borderRadius: '6px', fontSize: '11px', color: '#9ca3af' }}>
            🚛 {fleetData.filter(d => d.route).length} drivers on active routes
            {activeEmergencies.length > 0 && ` | 🚨 ${activeEmergencies.length} emergency active`}
          </div>
        </div>

        {/* RIGHT COLUMN: GOLDMINE FEED - Collapsible */}
        <div style={{ 
          width: isGoldminePanelOpen ? '280px' : '0px',
          minWidth: isGoldminePanelOpen ? '280px' : '0px',
          backgroundColor: '#111827',
          borderLeft: isGoldminePanelOpen ? '2px solid #374151' : 'none',
          overflow: 'hidden',
          transition: 'width 0.3s ease, min-width 0.3s ease',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            opacity: isGoldminePanelOpen ? 1 : 0,
            transition: 'opacity 0.2s ease'
          }}>
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#1f2937', 
              borderBottom: '1px solid #eab308',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: '#eab308', fontSize: '13px' }}>💰 GOLDMINE FEED</h3>
              <button 
                onClick={() => setIsGoldminePanelOpen(false)}
                style={{ 
                  backgroundColor: '#374151', 
                  color: '#9ca3af', 
                  border: 'none', 
                  padding: '4px 10px', 
                  borderRadius: '6px', 
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ▶
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
              {transactions && transactions.length > 0 ? transactions.map((tx, idx) => (
                <div key={`tx-${idx}`} style={{ padding: '10px', backgroundColor: '#1f2937', borderRadius: '6px', marginBottom: '8px', borderLeft: '4px solid #10b981' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: '#d1d5db' }}>{tx.driver_name}</span>
                    <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '12px' }}>+R{(Number(tx.amount) * 0.2).toFixed(2)}</span>
                  </div>
                </div>
              )) : (
                <p style={{ textAlign: 'center', color: '#4b5563', fontSize: '11px', marginTop: '20px' }}>Awaiting payments...</p>
              )}
            </div>
          </div>
          {/* Open button when collapsed */}
          {!isGoldminePanelOpen && (
            <button 
              onClick={() => setIsGoldminePanelOpen(true)}
              style={{
                position: 'absolute',
                top: '50%',
                right: '0',
                transform: 'translateY(-50%)',
                backgroundColor: '#374151',
                color: '#eab308',
                border: 'none',
                padding: '12px 6px',
                borderRadius: '8px 0 0 8px',
                cursor: 'pointer',
                writingMode: 'vertical-rl',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              GOLD ◀
            </button>
          )}
        </div>
      </div>

      {/* ==================== EMERGENCY LOCATE MODAL ==================== */}
      {showEmergencyModal && selectedEmergencyDriver && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
          <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '100%', border: '2px solid #ef4444' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '48px' }}>🚨</span>
              <h2 style={{ color: '#ef4444', margin: '10px 0 5px 0' }}>Emergency Locate</h2>
              <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                Trigger emergency locate for <strong style={{ color: 'white' }}>{selectedEmergencyDriver.driver_name}</strong>
              </p>
            </div>

            {/* Driver Info */}
            <div style={{ 
              backgroundColor: '#111827', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #374151'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: '#9ca3af' }}>Driver:</span>
                <span style={{ color: 'white', fontWeight: 'bold' }}>{selectedEmergencyDriver.driver_name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginTop: '4px' }}>
                <span style={{ color: '#9ca3af' }}>Status:</span>
                <span style={{ color: '#eab308' }}>{selectedEmergencyDriver.driver_status || 'Online'}</span>
              </div>
              {selectedEmergencyDriver.lat && selectedEmergencyDriver.lng && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginTop: '4px' }}>
                  <span style={{ color: '#9ca3af' }}>Location:</span>
                  <span style={{ color: '#60a5fa', fontSize: '11px' }}>
                    {selectedEmergencyDriver.lat.toFixed(6)}, {selectedEmergencyDriver.lng.toFixed(6)}
                  </span>
                </div>
              )}
            </div>

            {/* Reason Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#d1d5db', fontSize: '13px', display: 'block', marginBottom: '4px' }}>
                Reason for Emergency Locate
              </label>
              <input
                type="text"
                placeholder="e.g., Driver unresponsive, phone stolen..."
                value={emergencyReason}
                onChange={(e) => setEmergencyReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #374151',
                  backgroundColor: '#111827',
                  color: 'white',
                  boxSizing: 'border-box',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Warning */}
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              <p style={{ color: '#fca5a5', fontSize: '12px', margin: 0 }}>
                ⚠️ This will make the driver's phone ring loudly and display an emergency alert.
                The driver's location will be tracked in real-time.
              </p>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={() => {
                  setShowEmergencyModal(false);
                  setSelectedEmergencyDriver(null);
                  setEmergencyReason('');
                }}
                style={{ 
                  flex: 1, 
                  padding: '14px', 
                  backgroundColor: '#374151', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '6px', 
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                CANCEL
              </button>
              <button 
                onClick={() => {
                  triggerEmergencyLocate(
                    selectedEmergencyDriver.id,
                    selectedEmergencyDriver.driver_name,
                    emergencyReason
                  );
                }}
                disabled={isEmergencyTriggering}
                style={{ 
                  flex: 2, 
                  padding: '14px', 
                  backgroundColor: isEmergencyTriggering ? '#6b7280' : '#ef4444', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '6px', 
                  cursor: isEmergencyTriggering ? 'not-allowed' : 'pointer', 
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                {isEmergencyTriggering ? '⏳ TRIGGERING...' : '🚨 TRIGGER EMERGENCY'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DISPATCH MODAL */}
      {showDispatchModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', padding: '30px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '2px solid #eab308' }}>
            <h2 style={{ color: '#eab308', marginBottom: '20px' }}>🚛 New Customer Job</h2>
            
            {/* Pickup Address */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '4px' }}>📍 Pickup Address</label>
              <input
                type="text"
                placeholder="e.g., 123 Main St, Cape Town"
                value={dispatchForm.origin}
                onChange={(e) => debouncedOriginGeocode(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white', boxSizing: 'border-box' }}
              />
              {geocodingOrigin && <span style={{ fontSize: '11px', color: '#60a5fa' }}>⏳ Geocoding...</span>}
              {originCoordinates && <span style={{ fontSize: '11px', color: '#10b981' }}>✅ Coordinates found</span>}
            </div>

            {/* Delivery Address */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '4px' }}>📦 Delivery Address</label>
              <input
                type="text"
                placeholder="e.g., 456 Oak Ave, Johannesburg"
                value={dispatchForm.destination}
                onChange={(e) => debouncedDestGeocode(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white', boxSizing: 'border-box' }}
              />
              {geocodingDest && <span style={{ fontSize: '11px', color: '#60a5fa' }}>⏳ Geocoding...</span>}
              {destCoordinates && <span style={{ fontSize: '11px', color: '#10b981' }}>✅ Coordinates found</span>}
            </div>

            {/* Select Driver */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '4px' }}>👤 Assign Driver</label>
              <select
                value={dispatchForm.selectedDriverId}
                onChange={(e) => setDispatchForm(prev => ({ ...prev, selectedDriverId: e.target.value }))}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white' }}
              >
                <option value="">Select a driver...</option>
                {fleetData.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.driver_name} {driver.has_trailer ? '🔗' : ''} - R{driver.wallet_balance_zar}
                  </option>
                ))}
              </select>
            </div>

            {/* Trailer Toggle */}
            <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ color: '#9ca3af', fontSize: '12px' }}>🔗 Trailer Required</label>
              <input
                type="checkbox"
                checked={dispatchForm.requiresTrailer}
                onChange={(e) => setDispatchForm(prev => ({ ...prev, requiresTrailer: e.target.checked }))}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
            </div>

            {/* Inventory */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '4px' }}>📦 Inventory Items</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {Object.keys(dispatchForm.inventory).map(item => (
                  <div key={item}>
                    <label style={{ fontSize: '11px', color: '#9ca3af' }}>{item.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type="number"
                      min="0"
                      value={dispatchForm.inventory[item]}
                      onChange={(e) => setDispatchForm(prev => ({
                        ...prev,
                        inventory: { ...prev.inventory, [item]: parseInt(e.target.value) || 0 }
                      }))}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white' }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Special Instructions */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '4px' }}>📝 Special Instructions</label>
              <textarea
                placeholder="Any special instructions for the driver..."
                value={dispatchForm.special_instructions}
                onChange={(e) => setDispatchForm(prev => ({ ...prev, special_instructions: e.target.value }))}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white', boxSizing: 'border-box', minHeight: '60px' }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={() => { setShowDispatchModal(false); setDispatchForm({ origin: '', destination: '', originLat: '', originLng: '', destLat: '', destLng: '', selectedDriverId: '', requiresTrailer: false, inventory: { boxes: 0, fridge_double_door: 0, washing_machine: 0, queen_bed: 0 }, special_instructions: '' }); setOriginCoordinates(null); setDestCoordinates(null); }} 
                style={{ flex: 1, padding: '12px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                CANCEL
              </button>
              <button 
                onClick={createCustomerRequest} 
                style={{ flex: 2, padding: '12px', backgroundColor: '#eab308', color: 'black', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                CREATE JOB
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>
        {`
          @keyframes emergencyPulse {
            0% { opacity: 1; transform: scale(1); }
            100% { opacity: 0.7; transform: scale(1.1); }
          }
          
          .emergency-pulse {
            animation: emergencyPulse 0.5s ease-in-out infinite alternate;
          }
        `}
      </style>
    </div>
  );
};

export default AdminDashboard;
